import React, { useMemo, useState } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector/DateRangeSelector';
import { consoleTokens } from '@components/ui/console';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Divider, Popover, Stack, Typography } from '@mui/material';

import { toIsoDate } from '../utils/format';

interface PeriodPreset {
  key: string;
  label: string;
  range: () => { start: string; end: string };
}

const lastNDays = (days: number) => (): { start: string; end: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { start: toIsoDate(start), end: toIsoDate(end) };
};

const nextNDays = (days: number) => (): { start: string; end: string } => {
  const start = new Date();
  const end = new Date();
  end.setDate(start.getDate() + (days - 1));
  return { start: toIsoDate(start), end: toIsoDate(end) };
};

const singleDay = (offset: number) => (): { start: string; end: string } => {
  const day = new Date();
  day.setDate(day.getDate() + offset);
  return { start: toIsoDate(day), end: toIsoDate(day) };
};

// Forward ladder for schedule-style reports: the front desk lives in today
// and tomorrow.
const FORWARD_PRESETS: PeriodPreset[] = [
  { key: 'today', label: 'Today', range: singleDay(0) },
  { key: 'tomorrow', label: 'Tomorrow', range: singleDay(1) },
  { key: 'next7', label: 'Next 7 days', range: nextNDays(7) },
  { key: 'next30', label: 'Next 30 days', range: nextNDays(30) },
];

// The Stripe/Linear preset ladder: common rolling windows, calendar months,
// then a custom escape hatch. Payroll runs biweekly → 14 days is prominent.
const PRESETS: PeriodPreset[] = [
  { key: 'today', label: 'Today', range: singleDay(0) },
  { key: 'last7', label: 'Last 7 days', range: lastNDays(7) },
  { key: 'last14', label: 'Last 14 days', range: lastNDays(14) },
  { key: 'last30', label: 'Last 30 days', range: lastNDays(30) },
  {
    key: 'this_month',
    label: 'This month',
    range: () => {
      const now = new Date();
      return {
        start: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: toIsoDate(now),
      };
    },
  },
  {
    key: 'last_month',
    label: 'Last month',
    range: () => {
      const now = new Date();
      return {
        start: toIsoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
];

export interface PeriodPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  window: { start: string; end: string };
  onChange: (window: { start: string; end: string }) => void;
  /** Forward-looking ladder (Today/Tomorrow/Next…) for schedule reports. */
  forward?: boolean;
}

/** The `period:` token editor — preset windows first, custom range behind. */
export const PeriodPopover: React.FC<PeriodPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  window,
  onChange,
  forward = false,
}) => {
  const [customOpen, setCustomOpen] = useState(false);
  const presets = forward ? FORWARD_PRESETS : PRESETS;

  const activePresetKey = useMemo(() => {
    const match = presets.find(preset => {
      const range = preset.range();
      return range.start === window.start && range.end === window.end;
    });
    return match?.key ?? null;
  }, [window, presets]);

  const close = () => {
    setCustomOpen(false);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: customOpen ? 320 : 220 } } }}
    >
      <Box sx={{ py: 0.5 }}>
        {presets.map(preset => (
          <Stack
            direction="row"
            key={preset.key}
            onClick={() => {
              onChange(preset.range());
              close();
            }}
            sx={{
              alignItems: 'center',
              cursor: 'pointer',
              gap: 1,
              height: 30,
              px: 1.5,
              '&:hover': { bgcolor: consoleTokens.bandBg },
            }}
          >
            <Box sx={{ width: 16 }}>
              {activePresetKey === preset.key && (
                <CheckIcon sx={{ color: consoleTokens.ink, fontSize: 14 }} />
              )}
            </Box>
            <Typography sx={{ color: consoleTokens.ink, fontSize: '0.75rem' }}>
              {preset.label}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
              sx={{ color: consoleTokens.fainter, fontFamily: consoleTokens.mono, fontSize: '0.6875rem' }}
            >
              {preset.range().start.slice(5).replace('-', '/')}–
              {preset.range().end.slice(5).replace('-', '/')}
            </Typography>
          </Stack>
        ))}
      </Box>
      <Divider sx={{ borderColor: consoleTokens.line }} />
      <Stack
        direction="row"
        onClick={() => setCustomOpen(previous => !previous)}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          gap: 1,
          height: 30,
          px: 1.5,
          '&:hover': { bgcolor: consoleTokens.bandBg },
        }}
      >
        <Box sx={{ width: 16 }}>
          {activePresetKey === null && (
            <CheckIcon sx={{ color: consoleTokens.ink, fontSize: 14 }} />
          )}
        </Box>
        <Typography sx={{ color: consoleTokens.ink, fontSize: '0.75rem' }}>
          Custom range…
        </Typography>
      </Stack>
      {customOpen && (
        <Box sx={{ borderTop: `1px solid ${consoleTokens.line}`, p: 1.5 }}>
          <DateRangeSelector
            value={{
              start: new Date(`${window.start}T00:00:00`),
              end: new Date(`${window.end}T00:00:00`),
            }}
            onChange={range => {
              if (range.start && range.end) {
                onChange({ start: toIsoDate(range.start), end: toIsoDate(range.end) });
              }
            }}
            label="Date range"
            size="small"
            disableClearable
          />
        </Box>
      )}
    </Popover>
  );
};
